'use client';

import { ComponentProps, FC, ReactNode, Ref } from 'react';

import { SubmitButton } from '@/general/components/Button/SubmitButton';
import { FormGenericError } from '@/general/components/forms/FormGenericError/FormGenericError';
import { FormWithAction } from '@/general/components/forms/FormWithAction/FormWithAction';
import { isExternalUrl, processUrlParams } from '@/general/helpers/validateUrlParams';
import { useFormStepTransition } from '@/general/transitions/FormStepTransition/FormStepTransition.context';

type Props = Omit<ComponentProps<typeof FormWithAction>, 'onSuccess'> & {
  onSuccess?: ComponentProps<typeof FormWithAction>['onSuccess'];
  CustomFooter?: ReactNode;
  hideFormError?: boolean;
  formRef?: Ref<HTMLFormElement>;
  onSubmit?: (data: FormData) => void;
  dataTestId?: string;
  /** When set, replaces the default Continue label on the submit control. */
  submitLabel?: ReactNode;
};

export const StepForm: FC<Props> = ({
  action,
  renderContent,
  withRecaptcha,
  onSuccess,
  hideFormError,
  CustomFooter,
  formRef,
  onSubmit,
  dataTestId,
  submitLabel,
}) => {
  const { navigate } = useFormStepTransition();

  return (
    <FormWithAction
      dataTestId={dataTestId}
      action={action}
      withRecaptcha={withRecaptcha}
      onSubmit={onSubmit}
      formRef={formRef}
      onSuccess={(success) => {
        if (onSuccess) {
          onSuccess(success);
        }

        // Only navigate if the success message is a path or an external URL
        if (success.message.startsWith('/')) {
          navigate(success.message);
        }
        if (isExternalUrl(success.message)) {
          window.location.href = processUrlParams(success.message);
        }
      }}
      renderContent={(state) => (
        <>
          {renderContent(state)}
          {!hideFormError ? <FormGenericError formState={state} /> : null}
          {typeof CustomFooter !== 'undefined' ? (
            CustomFooter
          ) : (
            <footer className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <SubmitButton fullWidth data-testid="StepForm-submit">
                {submitLabel ?? 'Continue'}
              </SubmitButton>
            </footer>
          )}
        </>
      )}
    />
  );
};
